'use client';

import { type Dispatch, useReducer } from 'react';

import { AI_TOOLKIT_OPERATIONS, type VideoEditSubmitAction, type VideoEditSubmitState } from '../types';

export const VIDEO_EDIT_SUBMIT_DEFAULTS: VideoEditSubmitState = {
    pendingTaskId: null,
    originalVideoUrl: '',
    originalThumbnailUrl: '',
    duration: 0,
    aspectRatio: undefined,
    contentId: null,
    editedVideoUrl: '',
    completedTaskId: null,
    aiContentId: '',
    lastAiContentId: '',
    aiContentType: null,
    aiModelName: null,
    parentAiContentId: null,
    parentAiTaskId: null,
    operation: AI_TOOLKIT_OPERATIONS.GENERATE,
    status: 'idle',
    restoredFromHistory: false,
    failureReason: null,
    lastCompletedEdit: null,
};

/**
 * `completedTaskId` and `editedVideoUrl` always describe the same artifact, so every case below
 * clears or keeps the two together.
 */
export function videoEditSubmitReducer(
    state: VideoEditSubmitState,
    action: VideoEditSubmitAction,
): VideoEditSubmitState {
    switch (action.type) {
        case 'SELECTION':
            return {
                ...state,
                status: 'selection',
                operation: AI_TOOLKIT_OPERATIONS.GENERATE,
                editedVideoUrl: '',
                completedTaskId: null,
                originalVideoUrl: action.originalVideoUrl,
                originalThumbnailUrl: action.originalThumbnailUrl,
                duration: action.duration,
                aspectRatio: action.aspectRatio,
                contentId: action.contentId,
                startTime: undefined,
                restoredFromHistory: false,
                pendingTaskId: null,
                failureReason: null,
            };
        case 'GENERATION_IN_PROGRESS': {
            if (state.status === 'inProgress' && !action.pendingTaskId) return state;
            const operation = action.operation ?? AI_TOOLKIT_OPERATIONS.GENERATE;
            const isUpscale = operation === AI_TOOLKIT_OPERATIONS.UPSCALE;
            return {
                ...state,
                status: 'inProgress',
                operation,
                // An upscale runs on the edit we already produced: its task is the submission's
                // `parentTaskId`, and the url keeps the success view something to show.
                editedVideoUrl: isUpscale ? state.editedVideoUrl : '',
                completedTaskId: isUpscale ? state.completedTaskId : null,
                restoredFromHistory: false,
                pendingTaskId: action.pendingTaskId ?? null,
                aiContentId: action.aiContentId ?? state.aiContentId,
                lastAiContentId: action.lastAiContentId ?? state.lastAiContentId,
                aiContentType: action.aiContentType ?? state.aiContentType,
                aiModelName: action.aiModelName ?? state.aiModelName,
                startTime: action.startTime ?? state.startTime,
                duration: action.duration ?? state.duration,
                contentId: action.contentId ?? state.contentId,
                ...(isUpscale ? upscaleParent(state) : NO_PARENT),
                failureReason: null,
            };
        }
        case 'GENERATION_SUCCESS': {
            const aiContentType = action.aiContentType ?? state.aiContentType;
            const aiModelName = action.aiModelName ?? state.aiModelName;
            const operation = action.operation ?? state.operation;
            // Captured before `pendingTaskId` is cleared: this is the task an upscale of this
            // result will name as its `parentTaskId`.
            const completedTaskId = state.pendingTaskId;
            return {
                ...state,
                status: 'success',
                operation,
                editedVideoUrl: action.editedVideoUrl,
                completedTaskId,
                aiContentId: action.aiContentId,
                lastAiContentId: action.lastAiContentId,
                aiContentType,
                aiModelName,
                restoredFromHistory: false,
                pendingTaskId: null,
                failureReason: null,
                lastCompletedEdit: {
                    editedVideoUrl: action.editedVideoUrl,
                    completedTaskId,
                    operation,
                    originalVideoUrl: state.originalVideoUrl,
                    originalThumbnailUrl: state.originalThumbnailUrl,
                    aiContentId: action.aiContentId,
                    lastAiContentId: action.lastAiContentId,
                    aiContentType,
                    aiModelName,
                    startTime: state.startTime,
                    duration: state.duration,
                    contentId: state.contentId,
                    aspectRatio: state.aspectRatio,
                    parentAiContentId: state.parentAiContentId,
                    parentAiTaskId: state.parentAiTaskId,
                },
            };
        }
        case 'GENERATION_ERROR': {
            // The selected source video survives so the user can retry without re-selecting it. A
            // failed upscale additionally keeps the result it was upscaling, so the retry still has
            // its `parentTaskId`.
            // A submit that fails names the operation it was running; polling reports a failure for
            // whatever is already in flight, so it falls back to the recorded operation.
            const operation = action.operation ?? state.operation;
            const isUpscale = operation === AI_TOOLKIT_OPERATIONS.UPSCALE;
            return {
                ...state,
                status: 'error',
                operation,
                editedVideoUrl: isUpscale ? state.editedVideoUrl : '',
                completedTaskId: isUpscale ? state.completedTaskId : null,
                restoredFromHistory: false,
                pendingTaskId: null,
                aiContentType: null,
                aiModelName: null,
                failureReason: action.failureReason ?? null,
            };
        }
        case 'RESTORE_LAST_COMPLETED': {
            const snapshot = state.lastCompletedEdit;
            if (!snapshot) return state;
            return {
                ...state,
                ...snapshot,
                status: 'success',
                restoredFromHistory: false,
                pendingTaskId: null,
                failureReason: null,
            };
        }
        case 'EDIT_SELECTION':
            return {
                ...state,
                status: 'selection',
                operation: AI_TOOLKIT_OPERATIONS.GENERATE,
                editedVideoUrl: '',
                completedTaskId: null,
                restoredFromHistory: false,
                pendingTaskId: null,
                failureReason: null,
            };
        case 'RESET':
            return VIDEO_EDIT_SUBMIT_DEFAULTS;
    }
}

type VideoEditParent = Pick<VideoEditSubmitState, 'parentAiContentId' | 'parentAiTaskId'>;

const NO_PARENT: VideoEditParent = { parentAiContentId: null, parentAiTaskId: null };

/**
 * An upscale's parent is the edit in `completedTaskId`.
 * Its `aiContentId` is only current until the upscale's own IDs replace it, so a resubmitted upscale keeps the one it recorded.
 */
function upscaleParent(state: VideoEditSubmitState): VideoEditParent {
    const isResubmittedUpscale = state.operation === AI_TOOLKIT_OPERATIONS.UPSCALE;
    return {
        parentAiContentId: isResubmittedUpscale ? state.parentAiContentId : state.aiContentId || null,
        parentAiTaskId: state.completedTaskId,
    };
}

export function useVideoEditSubmit(
    initialState: VideoEditSubmitState = VIDEO_EDIT_SUBMIT_DEFAULTS,
): [VideoEditSubmitState, Dispatch<VideoEditSubmitAction>] {
    return useReducer(videoEditSubmitReducer, initialState);
}
